import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CarouselProvider } from 'pure-react-carousel';
import { useInView } from 'react-intersection-observer';

import BlockSection from '~/components/wrappers/BlockSection';

import FeaturesCarouselWithPhone from '~/components/features-carousel/FeaturesCarouselWithPhone';
import FeaturesCarouselDefault from '~/components/features-carousel/FeaturesCarousel';
import featuresCarouselReducer from '~/components/features-carousel/featuresCarouselReducer';

import { useSiteSettings } from '~/hooks/useSiteSettings';
import useWindowSize from '~/hooks/useWindowSize';

import { h3 } from '~/styles/typography';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes
} from '~/utilities/propTypes';

export const CarouselGrid = styled.div`
    display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'grid')};
    grid-column-gap: 30px;
    justify-content: space-between;
    justify-content: ${({ singleSlide }) =>
        singleSlide ? `center` : `space-between`};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: grid;
        grid-template-areas:
            '. image-slider'
            'text-slider image-slider'
            '. image-slider';
        grid-column-gap: 50px;
        grid-template-columns: ${({ singleSlide }) =>
            singleSlide ? `minmax(0, 530px) 350px` : `1fr 350px`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        grid-template-columns: ${({ singleSlide }) =>
            singleSlide ? `minmax(0, 530px) 400px` : `1fr 400px`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        grid-column-gap: 100px;
        grid-template-columns: ${({ singleSlide }) =>
            singleSlide ? `minmax(0, 530px) 500px` : `1fr 500px`};
    }
`;

export const TitleWrapper = styled.div`
    left: 0;
    z-index: 2;
    pointer-events: none;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        height: 100vh;
        top: 0;
        width: 100%;
        position: sticky;
    }
`;

export const Title = styled.h2`
    ${h3};
    top: 0;
    left: 0;
    width: 100%;
    text-transform: uppercase;
    padding: 60px 30px;
    grid-area: title;
    text-align: center;
    display: block;
    z-index: 2;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        position: absolute;
        width: ${({ noButtons }) => (noButtons ? '100%' : 'calc(100% - 75px)')};
        right: 0;
        left: auto;
        padding: 120px 0 100px 0;
        top: calc(50% - 100px);
        transform: translateY(-100%);
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        max-width: 530px;
        justify-self: flex-end;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        top: calc(50% - 120px);
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeDesktop}) {
        max-width: 650px;
        top: calc(50% - 200px);
    }
`;

export const TextSliderWrapper = styled(CarouselProvider)`
    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        grid-area: text-slider;
    }
`;

export const ImageSliderProvider = styled(CarouselProvider)`
    grid-area: image-slider;
    position: sticky;
    height: 100vh;
    top: 0;
    width: 350px;
    display: none;

    @media (min-height: 965px) {
        padding: 120px 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: block;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        width: 400px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        width: 500px;
    }
`;

const FeaturesCarousel = ({
    attributes = DefaultAcfAttributes,
    globalBlockOptions = DefaultGlobalBlockOptions,
    title,
    slides = [],
    mobileContent,
    usePhoneScreen = false
}) => {
    const { appLinks } = useSiteSettings();
    const sizes = useWindowSize();

    const [state, dispatch] = useReducer(featuresCarouselReducer, {
        currentSlide: 0,
        isSticky: false,
        isBottom: false,
        totalSlides: slides.length
    });

    const { inView, ref, entry } = useInView({
        threshold: 1 / slides.length
    });

    useEffect(() => {
        if (sizes.includes('tablet')) {
            if (inView) {
                dispatch({
                    type: 'SET_STICKY',
                    entry
                });
            } else {
                dispatch({
                    type: 'UNSET_STICKY',
                    entry
                });
            }
        } else {
            dispatch({
                type: 'UNSET_STICKY'
            });
        }
    }, [inView, entry, sizes]);

    return (
        <BlockSection
            {...{ attributes, globalBlockOptions, ref }}
            backgroundColor="red"
        >
            {usePhoneScreen ? (
                <FeaturesCarouselWithPhone
                    {...{
                        title,
                        slides,
                        mobileContent,
                        appLinks,
                        state,
                        dispatch
                    }}
                />
            ) : (
                <FeaturesCarouselDefault
                    {...{ title, slides, state, dispatch }}
                />
            )}
        </BlockSection>
    );
};

FeaturesCarousel.propTypes = {
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes,
    title: PropTypes.string.isRequired,
    slides: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            description: PropTypes.string,
            image: PropTypes.shape({
                id: PropTypes.string.isRequired,
                altText: PropTypes.string,
                sourceUrl: PropTypes.string.isRequired,
                localFile: PropTypes.shape({
                    childImageSharp: PropTypes.shape({
                        gatsbyImageData: PropTypes.object.isRequired
                    })
                })
            })
        })
    ),
    usePhoneScreen: PropTypes.bool,
    mobileContent: PropTypes.shape({
        title: PropTypes.string,
        features: PropTypes.arrayOf(
            PropTypes.shape({
                icon: PropTypes.string.isRequired,
                feature: PropTypes.string.isRequired
            })
        )
    })
};

export default FeaturesCarousel;
